/**
 * Meditation API Utilities
 *
 * Provides functions to interact with the async meditation generation API.
 * Replaces direct backendClient.functions.invoke('generate-meditation') calls.
 */

import { backendClient } from '@/lib/backendClient';

export interface MeditationGenerationParams {
  [key: string]: any;
}

export interface MeditationGenerationResult {
  script: string;
  content?: string; // Alias for backward compatibility
  session_length?: string;
  meditation_type?: string;
  job_id?: string;
  session_id?: string; // Session ID created by backend worker
  audio_url?: string; // URL to the generated audio file
  background_audio_url?: string; // URL to background audio (if requested)
  mixed_audio_url?: string; // URL to mixed audio (voice + background)
  mode?: string; // 'audio', 'text-only', or 'service_unavailable'
}

/**
 * Generate meditation using async job queue
 *
 * This function replaces: await backendClient.functions.invoke('generate-meditation', { body: params })
 *
 * It submits a job and polls for completion, maintaining backward compatibility
 * while using the async job queue infrastructure.
 *
 * @param params - Meditation generation parameters
 * @param options - Optional configuration
 * @returns Generated meditation script
 */
export async function generateMeditationAsync(
  params: MeditationGenerationParams,
  options?: {
    maxWaitSeconds?: number;
    pollInterval?: number;
    onProgress?: (progress: number, message: string) => void;
    onJobCreated?: (jobId: string) => void;
  }
): Promise<{ data: MeditationGenerationResult | null; error: any | null }> {
  // UX: Calculate dynamic timeout based on session complexity
  let calculatedMaxWaitSeconds = options?.maxWaitSeconds;
  if (!calculatedMaxWaitSeconds) {
    const baseTimeout = 180; // 3 minutes base
    const isGroupSession = params.session_scope === 'group' || params.group_session_id;
    const participantCount = params.participant_data?.length || 1;

    if (isGroupSession && participantCount > 3) {
      // Add 30 seconds per participant beyond the first 3
      const extraParticipants = participantCount - 3;
      calculatedMaxWaitSeconds = baseTimeout + (extraParticipants * 30);
      console.log(`🕒 Dynamic timeout: ${calculatedMaxWaitSeconds}s for ${participantCount} participants`);
    } else {
      calculatedMaxWaitSeconds = baseTimeout;
    }
  }

  const { maxWaitSeconds = calculatedMaxWaitSeconds, pollInterval = 1000, onProgress, onJobCreated } = options || {};

  try {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
    const useBackend = import.meta.env.VITE_USE_BACKEND === 'true';

    // Get auth token (different approach for backend vs Supabase)
    let authHeaders: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (useBackend) {
      // Backend auth uses cookies - no need for Authorization header
      // Cookies are sent automatically with credentials: 'include'
    } else {
      // Backend auth uses session token
      const { data: { session } } = await backendClient.auth.getSession();
      if (!session) {
        return {
          data: null,
          error: { message: 'Not authenticated', status: 401 }
        };
      }
      authHeaders['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Step 1: Submit job
    console.log('🚀 Submitting meditation generation job...');

    // CRITICAL FIX: Use correct endpoint based on session type
    // Practitioner-client sessions MUST use /practitioner/sessions
    // Regular sessions use /meditation/generate
    const isPractitionerSession = params.sessionType === 'practitioner-client' || params.client_id;
    const endpoint = isPractitionerSession
      ? `${backendUrl}/practitioner/sessions`
      : `${backendUrl}/meditation/generate`;

    console.log(`📍 Using endpoint: ${endpoint} (isPractitionerSession: ${isPractitionerSession})`);

    // Transform params for practitioner endpoint if needed
    let requestBody = params;
    if (isPractitionerSession) {
      // Practitioner endpoint expects different structure
      requestBody = {
        title: params.title || `${params.categoryId || 'Meditation'} Session`,
        session_notes: params.session_notes || params.additionalNotes || null,
        client_id: params.client_id || null,
        group_session_id: params.group_session_id || null,
        category_id: params.category_id || params.categoryId,
        session_length: params.session_length,
        voice_gender: params.voice_gender,
        specific_voice_id: params.specific_voice_id || params.specific_voice_id || params.voice_id,  // Standardized field name
        background_music_file: params.background_music_file,
        save_mixed_version: params.save_mixed_version || false,
        questionnaire_data: params.questionnaire_data || null,
        custom_prompt: params.custom_prompt || null,
        category_specific_data: {
          environment: params.environment,
          meditation_style: params.meditation_style,
          voice_style: params.voice_style,
          seeking_goal: params.seeking_goal,
          desired_feeling: params.desired_feeling,
          include_background_sounds: params.include_background_sounds,
          client_biography: params.client_biography,
          allowedNames: params.allowedNames,
          voiceTheraPreferences: params.voiceTheraPreferences,
        }
      };
    }

    const submitResponse = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders,
      credentials: 'include', // Include cookies for backend auth
      body: JSON.stringify(requestBody)
    });

    if (!submitResponse.ok) {
      const errorData = await submitResponse.json().catch(() => ({}));
      return {
        data: null,
        error: {
          message: errorData.detail || `HTTP ${submitResponse.status}`,
          status: submitResponse.status
        }
      };
    }

    const jobData = await submitResponse.json();
    // Practitioner endpoint returns {session_id, job_id, status}
    // Regular endpoint returns {id, ...}
    const jobId = jobData.job_id || jobData.id;
    const sessionId = jobData.session_id;

    console.log('✅ Job submitted:', jobId, sessionId ? `(session: ${sessionId})` : '');

    // Notify caller about job creation
    if (onJobCreated) {
      onJobCreated(jobId);
    }

    // Step 2: Poll for completion with exponential backoff
    const startTime = Date.now();
    let lastProgress = 0;
    let currentPollInterval = pollInterval; // Start with base interval (1s)
    const maxPollInterval = 10000; // Max 10 seconds between polls
    const backoffMultiplier = 1.2; // Gradual increase

    while (true) {
      // Check timeout
      const elapsed = (Date.now() - startTime) / 1000;
      if (elapsed > maxWaitSeconds) {
        return {
          data: null,
          error: {
            message: 'Generation timed out. Please try again.',
            status: 408,
            job_id: jobId
          }
        };
      }

      // Poll job status
      const statusResponse = await fetch(`${backendUrl}/jobs/job/${jobId}`, {
        headers: authHeaders,
        credentials: 'include' // Include cookies for backend auth
      });

      if (!statusResponse.ok) {
        return {
          data: null,
          error: {
            message: 'Failed to check job status',
            status: statusResponse.status
          }
        };
      }

      const statusData = await statusResponse.json();
      const { status, progress, result_data, error_message } = statusData;

      // Send progress update
      if (progress !== lastProgress && onProgress) {
        const message = status === 'processing'
          ? progress < 0.3 ? 'Generating script...'
          : progress < 0.7 ? 'Converting to audio...'
          : 'Finalizing...'
          : status === 'queued' ? 'Waiting in queue...'
          : '';

        onProgress(progress || 0, message);
        lastProgress = progress || 0;
      }

      // Check completion
      if (status === 'completed') {
        console.log('✅ Job completed successfully');

        // Extract result in format compatible with old API
        const result: MeditationGenerationResult = {
          script: result_data?.script_text || '',
          content: result_data?.script_text || '', // Alias for backward compat
          session_length: result_data?.session_length || params.session_length,
          meditation_type: result_data?.form_type || params.form_type,
          job_id: jobId,
          session_id: result_data?.session_id, // Session ID from backend worker
          audio_url: result_data?.audio_url,
          background_audio_url: result_data?.background_audio_url,
          mixed_audio_url: result_data?.mixed_audio_url,
          mode: result_data?.mode || 'audio'
        };

        return { data: result, error: null };
      }

      // Check service unavailable (DLQ)
      if (status === 'service_unavailable') {
        console.warn('⚠️ Job queued in DLQ - audio generation will retry automatically');
        return {
          data: {
            script: result_data?.script_text || '',
            content: result_data?.script_text || '',
            session_length: result_data?.session_length || params.session_length,
            meditation_type: result_data?.form_type || params.form_type,
            job_id: jobId,
            audio_url: undefined, // No audio yet
            mode: 'service_unavailable'
          },
          error: null
        };
      }

      // Check failure
      if (status === 'failed') {
        console.error('❌ Job failed:', error_message);
        return {
          data: null,
          error: {
            message: error_message || 'Generation failed',
            status: 500,
            job_id: jobId
          }
        };
      }

      // Wait before next poll with exponential backoff
      await new Promise(resolve => setTimeout(resolve, currentPollInterval));

      // Increase poll interval for next iteration (exponential backoff)
      currentPollInterval = Math.min(
        currentPollInterval * backoffMultiplier,
        maxPollInterval
      );
    }

  } catch (err) {
    console.error('❌ Generation error:', err);
    return {
      data: null,
      error: {
        message: err instanceof Error ? err.message : 'Unknown error',
        status: 500
      }
    };
  }
}

/**
 * Backward-compatible wrapper
 *
 * Drop-in replacement for: backendClient.functions.invoke('generate-meditation', { body: params })
 */
export async function invokeMeditationGeneration(params: MeditationGenerationParams) {
  return generateMeditationAsync(params);
}
